/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useContext, useState } from "react";
import { ContentZoneContext } from "cms/components/context";
import { RichTextPreview } from "cms/rich-text/simplicity-composer-tiptap";
import { EmptyContentMessage } from "cms/components/layout/empty/empty-content-message";
import { TextField, Button, FormGroup, Alert } from "@mui/material";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { isStringNotEmpty } from "utils/empty-exists";

const NewsletterCTAComponentDisplay = () => {
    const ctx = useContext(ContentZoneContext);
    const { component, hide } = ctx || {};
    const { token } = useAuth();

    const {
        heading,
        subheading,
        description,
        subscribe_label = "Subscribe",
        email_service_provider,
        selected_fields = [],
        email_group = null
    } = component || {};

    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const hasContent = isStringNotEmpty(heading) || 
                      isStringNotEmpty(subheading) || 
                      description ||
                      email_service_provider;

    const enabledFields = selected_fields.filter((field) => 
        typeof field === "object" ? field.enabled : true
    );

    const handleInputChange = (fieldName) => (event) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: event.target.value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!email_service_provider) {
            setSubmitStatus("error");
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await SimpliCityAdminSDK.email.createSubscriber(token, {
                providerKey: email_service_provider,
                firstName: formData.name,
                email: formData.email,
                city: formData.city,
                company: formData.company,
                country: formData.country,
                lastName: formData.lastName,
                phone: formData.phone,
                state: formData.state,
                z_i_p: formData.z_i_p,
                groups: email_group ? [email_group] : []
            });
            
            setSubmitStatus("success");
            setFormData({});
        } catch (error) {
            console.error("Newsletter subscription error:", error);
            setSubmitStatus("error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatFieldLabel = (fieldName) => {
        return fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/_/g, " ");
    };

    const isFieldRequired = (fieldName) => {
        return ["email", "name"].includes(fieldName);
    };

    return (
        <>
            {!hide && (
                <>
                    <EmptyContentMessage hasContent={hasContent} component={component} />
                    
                    {hasContent && (
                        <div className="newsletter-cta-component-display">
                            {heading && (
                                <h3 className="newsletter-cta-heading">{heading}</h3>
                            )}
                            
                            {subheading && (
                                <h4 className="newsletter-cta-subheading">{subheading}</h4>
                            )}
                            
                            {description && (
                                <div className="newsletter-cta-description">
                                    <RichTextPreview richtext={description} />
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="newsletter-cta-form">
                                {enabledFields.length > 0 && (
                                    <FormGroup className="newsletter-form-fields">
                                        {enabledFields.map((field) => {
                                            const fieldName = typeof field === "object" ? field.name : field;
                                            const isRequired = isFieldRequired(fieldName);
                                            
                                            return (
                                                <TextField
                                                    key={fieldName}
                                                    label={formatFieldLabel(fieldName)}
                                                    value={formData[fieldName] || ""}
                                                    onChange={handleInputChange(fieldName)}
                                                    required={isRequired}
                                                    type={fieldName === "email" ? "email" : "text"}
                                                    fullWidth
                                                    margin="normal"
                                                    variant="outlined"
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                )}

                                {submitStatus === "success" && (
                                    <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                                        Successfully subscribed to newsletter!
                                    </Alert>
                                )}

                                {submitStatus === "error" && (
                                    <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                                        Failed to subscribe. Please try again.
                                    </Alert>
                                )}

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSubmitting || !email_service_provider}
                                    className="newsletter-subscribe-button"
                                    sx={{ mt: 2 }}
                                >
                                    {isSubmitting ? "Subscribing..." : subscribe_label}
                                </Button>
                            </form>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

NewsletterCTAComponentDisplay.displayName = "NewsletterCTAComponentDisplay";
export { NewsletterCTAComponentDisplay }; 